/**
 * CardDesignPicker
 *
 * Fetches the design catalog for a given occasion type and renders
 * a horizontally-scrollable grid of CSS-rendered card previews.
 *
 * Props:
 *   occasionType — filters which designs are shown
 *   selected     — currently selected design ID (or null)
 *   onChange     — called with the new design ID (or null to deselect)
 *   disabled     — grays out while parent form is submitting
 */

import { useEffect, useState } from 'react';
import CardDesignPreview, { type DesignData } from './CardDesignPreview';
import CardInsidePreview from './CardInsidePreview';
import { apiFetch } from '../../lib/api';
import { sampleGreetingForOccasion } from '../../lib/greeting';

const CHARS_PER_PRINT_LINE = 45;
const MAX_PRINT_LINES      = 14;
const MAX_CHARS            = 650;

function countPrintLines(text: string): number {
  if (!text) return 0;
  return text.split('\n').reduce((total, line) => {
    return total + (line.length === 0 ? 1 : Math.ceil(line.length / CHARS_PER_PRINT_LINE));
  }, 0);
}

interface CardDesignPickerProps {
  occasionType:  string;
  selected:      string | null;
  onChange:      (id: string | null) => void;
  disabled?:     boolean;
  cardGreeting?: string;
  cardClosing?:  string;
  cardFromLine?: string;
  /** The user's chosen handwriting font id, so the inside preview matches the print. */
  cardFontId?:   number;
  /** True when the contact has card_type = 'handwritten' — enables cursive preview */
  handwritten?:  boolean;
}

export default function CardDesignPicker({
  occasionType,
  selected,
  onChange,
  disabled = false,
  cardGreeting,
  cardClosing,
  cardFromLine,
  cardFontId,
  handwritten = false,
}: CardDesignPickerProps) {

  const [designs,      setDesigns]      = useState<DesignData[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [expanded,     setExpanded]     = useState(false);
  const [showInside,   setShowInside]   = useState(false);
  const [sampleMsg,    setSampleMsg]    = useState('');

  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setFetchError(false);
    apiFetch<{ designs: DesignData[] }>(`/api/designs?occasion_type=${encodeURIComponent(occasionType)}`)
      .then((json) => setDesigns(json.designs ?? []))
      .catch((err) => { console.error('[CardDesignPicker] fetch failed:', err); setFetchError(true); })
      .finally(() => setLoading(false));
  }, [occasionType]);

  // Find the selected design object for the detail strip
  const selectedDesign = designs.find((d) => d.id === selected) ?? null;

  // Show first 5 by default; "See more" reveals the rest
  const INITIAL_COUNT = 5;
  const visible = expanded ? designs : designs.slice(0, INITIAL_COUNT);
  const hasMore  = !expanded && designs.length > INITIAL_COUNT;

  if (loading) {
    return (
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="w-[144px] h-[96px] rounded-[8px] bg-gray-100 animate-pulse flex-shrink-0" />
        ))}
      </div>
    );
  }

  if (fetchError) return (
    <p className="font-body text-xs text-gray-400 py-2">
      Couldn't load card designs, check your connection and refresh.
    </p>
  );

  if (designs.length === 0) return null;

  return (
    <div className={disabled ? 'opacity-50 pointer-events-none' : ''}>

      {/* Design grid — wraps to new rows so all cards are visible */}
      <div className="flex flex-wrap gap-3">
        {/* "No preference" tile */}
        <button
          type="button"
          onClick={() => onChange(null)}
          className={[
            'flex-shrink-0 w-[144px] h-[96px] rounded-[8px] border-2 flex flex-col items-center justify-center gap-1',
            'transition-all duration-150 hover:scale-105',
            selected === null
              ? 'border-sage bg-sage/10'
              : 'border-dashed border-gray-200 bg-white hover:border-gray-300',
          ].join(' ')}
        >
          {selected === null && (
            <div className="w-5 h-5 rounded-full bg-sage flex items-center justify-center mb-0.5">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          <span className="text-xl leading-none">✨</span>
          <p className="font-body text-[9px] font-semibold text-center text-gray-500 leading-tight px-1">
            We pick
          </p>
        </button>

        {visible.map((design) => (
          <CardDesignPreview
            key={design.id}
            design={design}
            size="sm"
            selected={selected === design.id}
            onClick={() => onChange(selected === design.id ? null : design.id)}
          />
        ))}

        {hasMore && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="flex-shrink-0 w-[144px] h-[96px] rounded-[8px] border-2 border-dashed
                       border-gray-200 bg-white hover:border-gray-300 flex flex-col items-center
                       justify-center gap-1 transition-all hover:scale-105"
          >
            <span className="text-xl leading-none">+{designs.length - INITIAL_COUNT}</span>
            <p className="font-body text-[9px] font-semibold text-center text-gray-500 leading-tight">
              more
            </p>
          </button>
        )}
      </div>

      {/* Selected design detail strip */}
      {selectedDesign && (
        <div className="mt-3 space-y-2">
          <div
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 border"
            style={{
              backgroundColor: `${selectedDesign.accent_color}10`,
              borderColor:     `${selectedDesign.accent_color}30`,
            }}
          >
            <span className="text-xl leading-none flex-shrink-0">{selectedDesign.emoji}</span>
            <div className="min-w-0 flex-1">
              <p className="font-body text-xs font-semibold text-navy">{selectedDesign.name}</p>
              <p className="font-body text-xs text-gray-500 mt-0.5 truncate">{selectedDesign.tagline}</p>
            </div>
            {/* Preview inside toggle */}
            <button
              type="button"
              onClick={() => setShowInside(!showInside)}
              className="flex-shrink-0 font-body text-[10px] font-semibold px-2 py-1 rounded-lg
                         transition-colors hover:bg-white/60"
              style={{ color: selectedDesign.accent_color }}
            >
              {showInside ? 'Hide' : '✍️ Preview inside'}
            </button>
            <button
              type="button"
              onClick={() => { onChange(null); setShowInside(false); setSampleMsg(''); }}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Deselect design"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Inline inside preview */}
          {showInside && (
            <div className="space-y-2">
              <CardInsidePreview
                design={selectedDesign}
                message={sampleMsg}
                cardGreeting={cardGreeting ?? sampleGreetingForOccasion(occasionType)}
                cardClosing={cardClosing}
                cardFromLine={cardFromLine}
                cardFontId={cardFontId}
                handwritten={handwritten}
              />
              <textarea
                value={sampleMsg}
                placeholder="Try a sample message to see how it looks inside…"
                rows={5}
                spellCheck
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= MAX_CHARS && countPrintLines(val) <= MAX_PRINT_LINES) {
                    setSampleMsg(val);
                  }
                }}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5
                           font-body text-sm text-navy placeholder-gray-400 resize-none
                           focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage/60
                           transition-colors"
              />
              {handwritten && /\p{Extended_Pictographic}/u.test(sampleMsg) && (
                <p className="font-body text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  ⚠️ Emojis can't be handwritten, they'll be removed before printing. Replace them with words instead.
                </p>
              )}
              {(() => {
                const lines   = countPrintLines(sampleMsg);
                const warn    = lines > 6 || sampleMsg.length > 280;
                const atLimit = lines >= MAX_PRINT_LINES || sampleMsg.length >= MAX_CHARS;
                return (
                  <p className={`font-body text-[10px] text-right ${atLimit ? 'text-red-500' : warn ? 'text-amber-600' : 'text-gray-400'}`}>
                    {lines}/{MAX_PRINT_LINES} lines · {sampleMsg.length}/{MAX_CHARS} chars
                    {atLimit ? ', limit reached' : warn ? ', near limit' : ''}
                  </p>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
